import { useState } from "react";
import { toast } from "sonner";
import api, { shareLink, errorText, repaintAndWait, patchGalleryCache } from "../lib/api";
import { renameDuck } from "../lib/renameDuck";
import { markCartSent } from "../lib/cartSent";
import { useAuth } from "../context/AuthContext";
import { useBasket } from "../context/BasketContext";
import { useDownload } from "./useDownload";
import { openShop } from "../lib/shopCheckout";

// Everything a DuckCard button can do (buy, basket, download, share, re-paint, restore, delete) — shared by the
// My Ducks sheet and the /my-ducks page so the two never drift apart.
export const useDuckActions = ({ onOpenItem, onDelete, onReload, onRenamed }) => {
  const { user } = useAuth();
  const basket = useBasket();
  const download = useDownload();
  const [busy, setBusy] = useState({});
  const [renamed, setRenamed] = useState({}); // id → optimistic name (cleared once the list refreshes)
  const mark = (key, id) => setBusy((b) => ({ ...b, [key]: id }));

  // Quick rename from a card: shows the new name at once, rolls back if the server refuses, then patches the list and the
  // cached first page in place with the server's sanitised name (no refetch, no cache clear, no paid call).
  const rename = async (c, name) => {
    const clean = name.trim();
    if (!clean || clean === (c.name || "")) return true;
    setRenamed((r) => ({ ...r, [c.id]: clean }));
    mark("renaming", c.id);
    try {
      const res = await renameDuck(c.id, clean);
      if (!res) { // "Cancel rename": nothing changed
        setRenamed((r) => ({ ...r, [c.id]: undefined }));
        return true;
      }
      const { data } = res;
      patchGalleryCache(c.id, { name: data.name });
      onRenamed?.(c.id, { name: data.name });
      setRenamed((r) => ({ ...r, [c.id]: onRenamed ? undefined : data.name }));
      toast.success("Duck renamed");
      return true;
    } catch (e) {
      setRenamed((r) => ({ ...r, [c.id]: undefined }));
      toast.error(e?.response?.data?.detail || "Could not rename this duck.");
      return false;
    } finally {
      mark("renaming", null);
    }
  };

  // 1-tap purchase at the default 50 mm. Signed in: through the basket; otherwise the classic add-to-cart link.
  const buy = async (c) => {
    mark("buying", c.id);
    try {
      if (user && basket && !c.deleted_at) {
        if (await basket.printNow(c.id, 50)) markCartSent(c.id, 50);
        return setTimeout(() => mark("buying", null), 3000);
      }
      const { data } = await api.get(`/share/${c.id}/order`, { params: { height: 70, colors: c.paint_opts?.colors || 8 } });
      if (data?.enabled && data?.url && data?.available !== false) {
        markCartSent(c.id, 50);
        openShop(data.url);
        return setTimeout(() => mark("buying", null), 3000);
      }
      toast.error(data?.available === false ? "This size is currently sold out." : "Ordering isn't available right now.");
    } catch {
      toast.error("Could not reach the shop. Please try again.");
    }
    mark("buying", null);
  };

  const repaint = async (c) => {
    mark("repainting", c.id);
    try {
      await repaintAndWait(c.id); // async repaint: start it, then poll until it finishes (or fails)
      toast.success("Colours refreshed — reopen this duck to see the update.");
      onReload?.();
    } catch (e) {
      toast.error(e?.message || errorText(e, "Could not re-paint this duck."));
    } finally {
      mark("repainting", null);
    }
  };

  const restore = async (c) => {
    mark("restoring", c.id);
    try {
      await api.post(`/creations/${c.id}/restore`);
      onReload?.();
      toast.success("Duck restored to My Ducks.");
    } catch {
      toast.error("Could not restore this duck.");
    } finally {
      mark("restoring", null);
    }
  };

  return {
    busy,
    renamed,
    rename,
    basketBusy: basket?.busy,
    open: onOpenItem,
    del: onDelete,
    buy,
    repaint,
    restore,
    basketAdd: (ids) => basket?.add(ids.map((duck_id) => ({ duck_id }))),
    download3mf: (c) => download.startDownload(c, "3mf", 50, c.paint_opts?.colors || 8),
    downloadStl: (c) => download.startDownload(c, "stl"),
    share: (c) => navigator.clipboard.writeText(shareLink(c.id)).then(() => toast.success("Share link copied!")),
    downloadState: download,
  };
};
